import { Layer, LayerProps } from 'shared/map-exports';
import { StyleFunctionProps } from 'features/mapping/mapping.utils';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'features/mapping/mapping.constants';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { ExternalFeatureLayerProps } from './ExternalFeatureLayer.types';
import { getLayerBeforeId } from './ExternalFeatureLayer.constants';
import {
  getLayerMetadata,
  getSelectedLayerId,
} from './ExternalFeatureLayer.utils';

type PointLayerProps = ExternalFeatureLayerProps;

export const getSelectedSymbolStyle = (
  props: StyleFunctionProps,
): LayerProps => {
  const { idKey, otherLayerProps = {}, selectedId } = props;
  return {
    ...otherLayerProps,
    type: 'symbol',
    layout: {
      'icon-image': 'CustomLayerLocationPin',
      'icon-allow-overlap': true,
      'icon-anchor': 'bottom',
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
    },
    filter: ['==', idKey, selectedId],
  };
};

/**
 * Renders whatever layers are defined in the `displayStyleData` of the feature layer, and:
 * - Adds a selected symbol layer that highlight the currently selected symbol.
 * - Uses the `clickListenerLayerId` to handle click events on the symbols.
 */
export const EmbeddedStylePointLayer = (
  props: PointLayerProps,
): JSX.Element => {
  const { sourceId, featureLayer, interactive } = props;
  const { shortName, displayStyleData } = featureLayer;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();

  if (displayStyleData.displayStyleType !== 'embedded') {
    throw new Error(
      'EmbeddedStyle*Layer requires displayStyleType to be embedded',
    );
  }

  const selectedId = getSelectedLayerId(sourceId, mapDrawerEntityForPoi);
  const selectedSymbolStyle = getSelectedSymbolStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
    },
    selectedId,
  });

  const metadata = getLayerMetadata(featureLayer, interactive);

  return (
    <>
      {displayStyleData.layers
        // Add the `source` property to each layer's style so it references the correct
        // <Source>.
        .map(
          (layer) => ({ ...layer, source: sourceId, metadata }) as LayerProps,
        )
        .map((layer) => (
          <Layer
            key={layer.id}
            {...layer}
            beforeId={getLayerBeforeId(featureLayer)}
          />
        ))}
      <Layer {...selectedSymbolStyle} />
    </>
  );
};
